import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { WhatsApp } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { BookingData } from "../../types/view-booking";
import { tokens } from "../../../../theme";
import CustomTextField from "../../../../components/ui/Textfield";
import { useShareTemplates } from "../../../configurations/share-messages/hooks/useShareMessageTemplates";

interface Props {
  open: boolean;
  onClose: () => void;
  bookingData?: BookingData | null;
}

export default function AnniversaryMessageModal({
  open,
  onClose,
  bookingData,
}: Props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const clientName = bookingData?.organiser?.name || "Family";
  const phone = bookingData?.organiser?.phone || "";
  const deceasedName = bookingData?.beloved?.name || "your loved one";
  const service = bookingData?.service || "";

  const [message, setMessage] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");

  const { templates, fetchTemplates } = useShareTemplates();

  const categoryMap: Record<string, string> = {
    Funeral: "funeral",
    Conference: "conference",
    Church: "church",
  };
  const templateCategory = categoryMap[service] ?? "";

  const safeTemplates = Array.isArray(templates) ? templates : [];
  const anniversaryTemplates = safeTemplates.filter(
    (t) => t.template_type === "anniversary_message",
  );

  useEffect(() => {
    if (open && bookingData) {
      fetchTemplates(templateCategory, "anniversary_message");
    }
  }, [open, bookingData, fetchTemplates, templateCategory]);

  useEffect(() => {
    if (!open) {
      setSelectedTemplateId("");
      setMessage("");
    }
  }, [open]);

  const fillTemplate = (body: string) => {
    const values: Record<string, string> = {
      clientName,
      deceasedName,
      companyName: "Muzukuru",
      eventName: service,
      bookingReference: String(
        bookingData?.reference_number ?? bookingData?.id ?? "N/A",
      ),
    };
    let filled = body;
    Object.entries(values).forEach(([key, val]) => {
      filled = filled.replaceAll(`{${key}}`, val);
    });
    return filled;
  };

  const handleTemplateChange = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = anniversaryTemplates.find((t) => t.id === templateId);
    if (template) {
      setMessage(fillTemplate(template.body_template));
    }
  };

  useEffect(() => {
    if (open && anniversaryTemplates.length > 0 && !selectedTemplateId) {
      const first = anniversaryTemplates[0];
      setSelectedTemplateId(first.id);
      setMessage(fillTemplate(first.body_template));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anniversaryTemplates.length]);

  const handleShareWhatsApp = () => {
    const encoded = encodeURIComponent(message);
    const phoneClean = phone.replace(/\D/g, "");
    window.open(`https://wa.me/${phoneClean}?text=${encoded}`, "_blank");
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, maxWidth: 500, p: 1 } }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: 18, pb: 1 }}>
        Anniversary message
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
          Sending to {clientName} • {phone || "No phone on record"}
        </Typography>

        {/* Template Selector */}
        {anniversaryTemplates.length > 0 ? (
          <CustomTextField
            label="Message Template"
            fullWidth
            select
            value={selectedTemplateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            sx={{ mb: 2 }}
          >
            {anniversaryTemplates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
          </CustomTextField>
        ) : (
          <Typography
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              mb: 2,
              p: 1.5,
              bgcolor: colors.primary[800],
              borderRadius: 2,
              textAlign: "center",
            }}
          >
            No {templateCategory} anniversary templates found. Create one in
            configurations.
          </Typography>
        )}

        <TextField
          fullWidth
          multiline
          minRows={8}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: colors.primary[800],
              fontSize: 13,
              lineHeight: 1.6,
              "& fieldset": { border: "none" },
            },
          }}
        />

        <Button
          variant="contained"
          fullWidth
          onClick={handleShareWhatsApp}
          startIcon={<WhatsApp />}
          sx={{
            bgcolor: "#25D366",
            color: "white",
            textTransform: "none",
            borderRadius: 2,
            py: 1.2,
            fontSize: 14,
            fontWeight: 500,
            boxShadow: "none",
            "&:hover": { bgcolor: "#20bd5a", boxShadow: "none" },
          }}
        >
          Share via WhatsApp
        </Button>
      </DialogContent>
    </Dialog>
  );
}
